import { CARD_LINK } from '@/components/agent-chat/agent-card.component';
import { CardText } from '@/components/agent-chat/card-parts.component';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type {
  PlayProposalPreview,
  PlayProposalRequirement,
} from '@/types/gtm-agents';
import { creditWindowLabel, RECORDS_PER_RUN } from './limit-words';
import { PanelButton } from './panel-ui.component';
import type { ProposalReviewState } from './play-proposal-review.util';
import { proposalIsBlocked } from './play-proposal-review.util';
import { ReviewSummary } from './review-summary.component';
import { proposalReviewSummary } from './review-summary.util';

/**
 * Read-only findings stay available while a candidate is blocked, with the
 * API's preview of its credits and limits when it has one. `link` is Clu's
 * card's trigger, its Details link; `open` lets the card open it from its
 * warnings too.
 */
export function PlayProposalDetails({
  proposal,
  link = false,
  summary,
  open,
  onOpenChange,
}: {
  proposal: ProposalReviewState & {
    preview?: PlayProposalPreview | null;
    /** The run limits the product filled in ("creditsPerRun"). */
    defaultedLimits?: readonly string[];
  };
  link?: boolean;
  summary?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const findings = [
    ...(proposal.blockedReasons ?? []).map((reason) => ({
      ...reason,
      severity: 'error' as const,
    })),
    ...(proposal.issues ?? []),
  ].filter(
    (issue, index, all) =>
      all.findIndex(
        (other) =>
          other.code === issue.code &&
          other.nodeId === issue.nodeId &&
          other.message === issue.message
      ) === index
  );
  const requirements = proposal.requirements ?? [];
  const fullSummary = link ? summary : undefined;
  const preview = proposal.preview;
  if (!fullSummary && !findings.length && !requirements.length && !preview)
    return null;
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {link ? (
          <button type='button' className={CARD_LINK}>
            Details
          </button>
        ) : (
          <PanelButton>Review details</PanelButton>
        )}
      </PopoverTrigger>
      <PopoverContent
        align='end'
        className='max-h-[360px] w-[360px] max-w-[calc(100vw-32px)] overflow-y-auto p-[12px]'
        aria-label='Candidate review details'
      >
        {fullSummary ? (
          <CardText
            lines={0}
            className='mb-[12px] break-words whitespace-pre-wrap'
          >
            {fullSummary}
          </CardText>
        ) : null}
        <ReviewSummary
          label='Candidate summary'
          rows={proposalReviewSummary(proposal, proposalIsBlocked(proposal))}
        />
        {findings.length > 0 && (
          <ul
            aria-label='Candidate issues'
            className='mt-[12px] flex flex-col gap-[8px] text-[12px] leading-[16px]'
          >
            {findings.map((issue, index) => (
              <li
                key={index}
                className={cn(
                  issue.severity === 'error' ||
                    ('blocks' in issue && issue.blocks)
                    ? 'text-destructive'
                    : 'text-muted-foreground'
                )}
              >
                <span className='font-medium'>
                  {issue.severity === 'error' ||
                  ('blocks' in issue && issue.blocks)
                    ? 'Blocked'
                    : 'Warning'}
                </span>
                <p>{issue.message}</p>
              </li>
            ))}
          </ul>
        )}
        {preview && (
          <PreviewCredits
            preview={preview}
            defaultedLimits={proposal.defaultedLimits}
          />
        )}
        <RequirementEvidence requirements={requirements} />
      </PopoverContent>
    </Popover>
  );
}

function amount(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value.toLocaleString(undefined, { maximumFractionDigits: 6 })
    : 'Unverified';
}

/**
 * The API's preview of a proposal's credits and limits, a limit the product
 * filled in marked "default", and what the preview could not check.
 */
function PreviewCredits({
  preview,
  defaultedLimits,
}: {
  preview: PlayProposalPreview;
  defaultedLimits?: readonly string[];
}) {
  const note = (limit: string) =>
    defaultedLimits?.includes(limit) ? ' · default' : '';
  const { credits } = preview;
  const rows = [
    { label: 'Maximum per record', value: amount(credits.perRecordMax) },
    { label: 'Maximum per company', value: amount(credits.perCompanyMax) },
    { label: 'Estimated maximum per run', value: amount(credits.perRunMax) },
    {
      label: creditWindowLabel(credits.capWindow),
      value: amount(credits.capPerRun) + note('creditsPerRun'),
    },
    {
      label: RECORDS_PER_RUN,
      value: amount(credits.recordsPerRun) + note('recordsPerRun'),
    },
  ];
  return (
    <div className='mt-[12px] border-t border-border pt-[12px] text-[12px] leading-[16px]'>
      <ul aria-label='Credits and limits' className='space-y-[4px]'>
        {rows.map((row, index) => (
          <li
            key={index}
            className='flex flex-wrap justify-between gap-x-[12px] gap-y-[2px]'
          >
            <span className='text-muted-foreground'>{row.label}</span>
            <span className='text-foreground'>{row.value}</span>
          </li>
        ))}
      </ul>
      {preview.warnings.length > 0 && (
        <div className='mt-[12px]'>
          <span className='font-medium text-foreground'>Needs checking</span>
          <ul
            aria-label='Needs checking'
            className='mt-[4px] flex flex-col gap-[4px] text-muted-foreground'
          >
            {preview.warnings.map((warning, index) => (
              <li key={index} className='break-words'>
                {warning}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Each requirement line, and whether the evidence supports it. */
export function RequirementEvidence({
  requirements,
}: {
  requirements: readonly PlayProposalRequirement[];
}) {
  if (!requirements.length) return null;
  return (
    <details className='mt-[12px] border-t border-border pt-[12px]'>
      <summary className='cursor-pointer text-[12px] text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden'>
        Requirement evidence
      </summary>
      <ul
        aria-label='Requirements'
        className='mt-[12px] flex flex-col gap-[8px] text-[12px] leading-[16px]'
      >
        {requirements.map((requirement) => (
          <li key={requirement.id}>
            <p className='break-words whitespace-pre-wrap text-foreground'>
              {requirement.text}
            </p>
            <p className='text-muted-foreground'>
              {requirement.state === 'user_confirmed'
                ? 'Confirmed'
                : 'Inferred'}
              {' · '}
              {requirement.coverage === 'unverified'
                ? 'Unverified'
                : 'Supported'}
            </p>
          </li>
        ))}
      </ul>
    </details>
  );
}
